const UINT32_MODULUS = 4294967296

export function keepDeterministicSample(idempotencyKey, version, rate) {
  if (rate <= 0) return false
  if (rate >= 1) return true

  let hash = 2166136261
  const bytes = new TextEncoder().encode(`${version}:${idempotencyKey}`)
  for (const byte of bytes) {
    hash ^= byte
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return (hash / UINT32_MODULUS) < rate
}

// Browser-independent transport/buffer for L3 map telemetry. DOM/map state stays in the controller;
// timing, deterministic sampling, coalescing and delivery live here and are runtime-testable in Node.
export class MapTelemetryClient {
  constructor({
    isEnabled,
    endpoint,
    config,
    buildEvent,
    fetchFn = globalThis.fetch?.bind(globalThis),
    sendBeaconFn = globalThis.navigator?.sendBeacon?.bind(globalThis.navigator),
    BlobClass = globalThis.Blob,
    setTimer = globalThis.setTimeout.bind(globalThis),
    clearTimer = globalThis.clearTimeout.bind(globalThis)
  }) {
    this.isEnabled = isEnabled
    this.endpoint = endpoint
    this.getConfig = config
    this.buildEvent = buildEvent
    this.fetchFn = fetchFn
    this.sendBeaconFn = sendBeaconFn
    this.BlobClass = BlobClass
    this.setTimer = setTimer
    this.clearTimer = clearTimer
    this.pending = []
    this.legacyTimers = new Map()
    this.viewportTimer = null
    this.flushTimer = null
    this.sequence = 0
  }

  get pendingCount() {
    return this.pending.length
  }

  scheduleViewport(legacyEventType) {
    if (!this.isEnabled()) return

    const config = this.getConfig()
    if (!config.R2_ENABLED) {
      this.scheduleLegacy(legacyEventType)
      return
    }

    if (this.viewportTimer) this.clearTimer(this.viewportTimer)
    this.viewportTimer = this.setTimer(() => {
      this.viewportTimer = null
      this.send("map.viewport")
    }, config.VIEWPORT_SETTLE_MS ?? 1000)
  }

  scheduleLegacy(eventType) {
    const previous = this.legacyTimers.get(eventType)
    if (previous) this.clearTimer(previous)

    const config = this.getConfig()
    const timer = this.setTimer(() => {
      this.legacyTimers.delete(eventType)
      const event = this.nextEvent(eventType)
      if (event) this.sendLegacy(event)
    }, config.VIEWPORT_SETTLE_MS ?? 1000)
    this.legacyTimers.set(eventType, timer)
  }

  send(eventType) {
    if (!this.isEnabled()) return

    const config = this.getConfig()
    if (!config.R2_ENABLED) {
      this.scheduleLegacy(eventType)
      return
    }

    const event = this.nextEvent(eventType)
    if (!event) return

    const version = config.SAMPLING_VERSION ?? "viewport-v1"
    const rate = config.SAMPLE_RATES?.[eventType] ?? 1
    if (!keepDeterministicSample(event.idempotency_key, version, rate)) return
    event.sampling_version = version

    if (eventType === "map.viewport") {
      const existing = this.pending.findIndex((item) => item.event_type === "map.viewport")
      if (existing >= 0) this.pending[existing] = event
      else this.pending.push(event)
    } else {
      this.pending.push(event)
    }

    const maxBatch = config.MAX_BATCH_SIZE ?? 10
    if (this.pending.length >= maxBatch) {
      this.clearFlushTimer()
      this.flush()
    } else if (!this.flushTimer) {
      this.flushTimer = this.setTimer(() => {
        this.flushTimer = null
        this.flush()
      }, config.BATCH_FLUSH_MS ?? 250)
    }
  }

  sendLegacy(event) {
    this.fetchKeepalive(JSON.stringify(event))
  }

  flush({ useBeacon = false } = {}) {
    this.clearFlushTimer()
    if (!this.isEnabled()) {
      this.pending = []
      return
    }

    const config = this.getConfig()
    const maxBatch = config.MAX_BATCH_SIZE ?? 10
    while (this.pending.length > 0) {
      const events = this.pending.splice(0, maxBatch)
      const body = JSON.stringify({ events })
      const sent = useBeacon && this.sendBeaconFn && this.sendBeaconFn(
        this.endpoint(),
        new this.BlobClass([ body ], { type: "application/json" })
      )
      if (!sent) this.fetchKeepalive(body)
    }
  }

  pagehide() {
    if (this.viewportTimer) {
      this.clearTimer(this.viewportTimer)
      this.viewportTimer = null
      this.send("map.viewport")
    }
    this.flush({ useBeacon: true })
  }

  disconnect() {
    this.legacyTimers.forEach((timer) => this.clearTimer(timer))
    this.legacyTimers.clear()
    this.pagehide()
  }

  nextEvent(eventType) {
    if (!this.isEnabled()) return null

    this.sequence += 1
    return this.buildEvent(eventType, this.sequence)
  }

  fetchKeepalive(body) {
    if (!this.fetchFn) return

    try {
      Promise.resolve(this.fetchFn(this.endpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "same-origin"
      })).catch(() => {})
    } catch (_error) {
      // Telemetry delivery is intentionally best-effort.
    }
  }

  clearFlushTimer() {
    if (!this.flushTimer) return

    this.clearTimer(this.flushTimer)
    this.flushTimer = null
  }
}
